import {
  faBan,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * The status mark shared by stages and their subtasks in the flow tracker. Takes the
 * raw status from either vocabulary — stages (`pending | active | done | skipped`) and
 * tasks (`open | done | blocked | cancelled`) — and maps both onto one visual set of
 * solid circle glyphs sized to fill the mark:
 *
 * - `done` — filled circle-check in working ink
 * - `active` — live dot inside a working-ink halo, pulsing
 * - `blocked` — stopped-ink ban
 * - `skipped` / `cancelled` — faint circle-x (paired with a struck label)
 * - `pending` / `open` / anything else — empty hollow ring (not reached yet)
 */
export const StatusMark = ({
  status,
  size = "md",
  stale = false,
}: {
  status: string;
  size?: "md" | "sm";
  /** Stale visit: `active` marks where work stopped, so it flags, not green. */
  stale?: boolean;
}) => {
  const box = size === "md" ? "h-[18px] w-[18px]" : "h-3.5 w-3.5";
  const glyph = size === "md" ? "text-[18px]" : "text-[14px]";

  if (status === "done")
    return (
      <FontAwesomeIcon
        icon={faCircleCheck}
        aria-hidden="true"
        className={`${glyph} shrink-0 text-ink-working`}
      />
    );

  if (status === "active")
    return (
      <span
        aria-hidden="true"
        className={`flex ${box} shrink-0 items-center justify-center rounded-full ${
          stale ? "bg-ink-flag/15" : "bg-ink-working/15"
        }`}
      >
        <span
          className={`size-1.5 rounded-full [animation:bds-pulse_1.6s_ease-in-out_infinite] motion-reduce:animate-none ${
            stale
              ? "bg-wash-flag ring-1 ring-ink-flag"
              : "bg-wash-working ring-1 ring-ink-working"
          }`}
        />
      </span>
    );

  if (status === "blocked")
    return (
      <FontAwesomeIcon
        icon={faBan}
        aria-hidden="true"
        className={`${glyph} shrink-0 text-ink-stopped`}
      />
    );

  if (status === "skipped" || status === "cancelled")
    return (
      <FontAwesomeIcon
        icon={faCircleXmark}
        aria-hidden="true"
        className={`${glyph} shrink-0 text-faint`}
      />
    );

  return (
    <span
      aria-hidden="true"
      className={`${box} shrink-0 rounded-full border-2 border-faint`}
    />
  );
};
